import {
  type Meta,
  type VoucherDetailsErrors,
  type VoucherDetailsFields
} from "@/lib/types";
import { sanitizeMetadata, validateMeta } from "@/lib/utils";
import { submitVouch } from "@/services";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useSessionStorage } from "./use-session-storage";

const initialMeta = {
  Accuracy: undefined as boolean | undefined,
  Endorsement: undefined as boolean | undefined,
  Comments: "",
};

export const useQualifyVouchMutation = (firstName?: string) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { vouchId } = useParams<{ vouchId: string }>();

  const { getSessionStore, setSessionStore, clearSessionStore } =
    useSessionStorage();

  const [meta, setMeta] = useState<typeof initialMeta>(() =>
    getSessionStore(vouchId!, initialMeta),
  );
  const [fieldError, setFieldError] = useState<VoucherDetailsErrors>({});

  const clearFieldError = (field: keyof VoucherDetailsFields) =>
    setFieldError((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const updateMeta = (patch: Partial<typeof initialMeta>) => {
    if (patch.Comments !== undefined) clearFieldError("Comments");
    setMeta((prev) => ({ ...prev, ...patch }));
  };

  const submitVouchMutation = useMutation({
    mutationFn: submitVouch,
    onSuccess: (data) => {
      if (data.ApiHttpResponse === 202) {
        clearSessionStore(vouchId!);
        navigate("../complete", {
          state: { negative: true, vouchee: { firstName } },
        });
        queryClient.invalidateQueries({ queryKey: ["vouch-session"] });
      }
    },
  });

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    const sanitizedMeta = sanitizeMetadata(meta);

    // Both positive: continue to the respond step. Otherwise submit the vouch.
    if (sanitizedMeta.Accuracy && sanitizedMeta.Endorsement) {
      setSessionStore(vouchId!, JSON.stringify(sanitizedMeta));
      navigate("../respond");
    } else {
      const errors = validateMeta({ Comments: meta.Comments });
      if (errors.Comments) {
        setFieldError(errors);
        return;
      }
    
      submitVouchMutation.mutate({
        VouchId: vouchId!,
        Meta: sanitizedMeta as Meta,
      });
    }
  };

  return {
    accuracyValue:
      meta.Accuracy === undefined ? "" : meta.Accuracy ? "yes" : "no",
    endorsementValue:
      meta.Endorsement === undefined ? "" : meta.Endorsement ? "yes" : "no",
    comments: meta.Comments ?? "",
    fieldError,
    updateMeta,
    handleSubmit,
    isSubmitDisabled:
      meta.Accuracy === undefined ||
      meta.Endorsement === undefined ||
      ((meta.Accuracy === false || meta.Endorsement === false) &&
        !meta.Comments?.trim()),
    isSubmitting: submitVouchMutation.isPending,
  };
};
